"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RecursiveOperationManager = void 0;
const DhtRpc_1 = require("../../../generated/packages/dht/protos/DhtRpc");
const RoutingSession_1 = require("../routing/RoutingSession");
const utils_1 = require("@streamr/utils");
const RecursiveOperationSessionRpcRemote_1 = require("./RecursiveOperationSessionRpcRemote");
const RecursiveOperationSession_1 = require("./RecursiveOperationSession");
const ListeningRpcCommunicator_1 = require("../../transport/ListeningRpcCommunicator");
const DhtRpc_client_1 = require("../../../generated/packages/dht/protos/DhtRpc.client");
const SortedContactList_1 = require("../contact/SortedContactList");
const getPreviousPeer_1 = require("../routing/getPreviousPeer");
const RouterRpcLocal_1 = require("../routing/RouterRpcLocal");
const RecursiveOperationRpcLocal_1 = require("./RecursiveOperationRpcLocal");
const identifiers_1 = require("../../identifiers");
const PeerManager_1 = require("../PeerManager");
const logger = new utils_1.Logger(module);
class RecursiveOperationManager {
    ongoingSessions = new Map();
    stopped = false;
    options;
    constructor(options) {
        this.options = options;
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new RecursiveOperationRpcLocal_1.RecursiveOperationRpcLocal({
            doRouteRequest: (routedMessage) => this.doRouteRequest(routedMessage),
            addContact: (contact) => this.options.addContact(contact),
            isMostLikelyDuplicate: (requestId) => this.options.router.isMostLikelyDuplicate(requestId),
            addToDuplicateDetector: (requestId) => this.options.router.addToDuplicateDetector(requestId)
        });
        this.options.rpcCommunicator.registerRpcMethod(DhtRpc_1.RouteMessageWrapper, DhtRpc_1.RouteMessageAck, 'routeRequest', async (routedMessage) => {
            if (this.stopped) {
                return (0, RouterRpcLocal_1.createRouteMessageAck)(routedMessage, DhtRpc_1.RouteMessageError.STOPPED);
            }
            else {
                return rpcLocal.routeRequest(routedMessage);
            }
        });
    }
    async execute(targetId, operation, excludedPeer, waitForCompletion = true) {
        if (this.stopped) {
            return { closestNodes: [] };
        }
        const session = new RecursiveOperationSession_1.RecursiveOperationSession({
            transport: this.options.sessionTransport,
            targetId,
            localPeerDescriptor: this.options.localPeerDescriptor,
            // TODO use options option or named constant?
            waitedRoutingPathCompletions: this.options.connectionsView.getConnectionCount() > 1 ? 2 : 1,
            operation,
            // TODO would it make sense to give excludedPeer as one of the fields RecursiveOperationSession?
            doRouteRequest: (routedMessage) => {
                return this.doRouteRequest(routedMessage, excludedPeer);
            }
        });
        if (this.options.connectionsView.getConnectionCount() === 0) {
            const dataEntries = Array.from(this.options.localDataStore.values(targetId));
            session.onResponseReceived((0, identifiers_1.toNodeId)(this.options.localPeerDescriptor), [this.options.localPeerDescriptor], [this.options.localPeerDescriptor], dataEntries, true);
            return session.getResults();
        }
        this.ongoingSessions.set(session.getId(), session);
        if (waitForCompletion === true) {
            try {
                await (0, utils_1.runAndWaitForEvents3)([() => session.start(this.options.serviceId)], [[session, 'completed']], 
                // TODO use options option or named constant?
                RecursiveOperationSession_1.RECURSIVE_OPERATION_TIMEOUT);
            }
            catch (err) {
                logger.debug('start failed', { err });
            }
        }
        else {
            session.start(this.options.serviceId);
            // Wait for delete operation to be sent out by the router
            // TODO: Add a feature to wait for the router to pass the message?
            await (0, utils_1.wait)(50);
        }
        if (operation === DhtRpc_1.RecursiveOperation.FETCH_DATA) {
            const dataEntries = Array.from(this.options.localDataStore.values(targetId));
            if (dataEntries.length > 0) {
                this.sendResponse([this.options.localPeerDescriptor], this.options.localPeerDescriptor, session.getId(), [], dataEntries, true);
            }
        }
        else if (operation === DhtRpc_1.RecursiveOperation.DELETE_DATA) {
            this.options.localDataStore.markAsDeleted(targetId, (0, identifiers_1.toNodeId)(this.options.localPeerDescriptor));
        }
        this.ongoingSessions.delete(session.getId());
        session.stop();
        return session.getResults();
    }
    sendResponse(routingPath, targetPeerDescriptor, serviceId, closestConnectedNodes, dataEntries, noCloserNodesFound = false) {
        const isOwnNode = (0, identifiers_1.areEqualPeerDescriptors)(this.options.localPeerDescriptor, targetPeerDescriptor);
        if (isOwnNode && this.ongoingSessions.has(serviceId)) {
            this.ongoingSessions.get(serviceId)
                .onResponseReceived((0, identifiers_1.toNodeId)(this.options.localPeerDescriptor), routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound);
        }
        else {
            // TODO use options option or named constant?
            const remoteCommunicator = new ListeningRpcCommunicator_1.ListeningRpcCommunicator(serviceId, this.options.sessionTransport, { rpcRequestTimeout: RecursiveOperationSession_1.RECURSIVE_OPERATION_TIMEOUT });
            const rpcRemote = new RecursiveOperationSessionRpcRemote_1.RecursiveOperationSessionRpcRemote(this.options.localPeerDescriptor, targetPeerDescriptor, remoteCommunicator, DhtRpc_client_1.RecursiveOperationSessionRpcClient, 
            // TODO use options option or named constant?
            10000);
            rpcRemote.sendResponse(routingPath, closestConnectedNodes, dataEntries, noCloserNodesFound);
            remoteCommunicator.destroy();
        }
    }
    doRouteRequest(routedMessage, excludedPeer) {
        if (this.stopped) {
            return (0, RouterRpcLocal_1.createRouteMessageAck)(routedMessage, DhtRpc_1.RouteMessageError.STOPPED);
        }
        const targetId = (0, identifiers_1.toDhtAddress)(routedMessage.target);
        const request = routedMessage.message.body.recursiveOperationRequest;
        // TODO use options option or named constant?
        const closestConnectedNodes = this.getClosestConnectedNodes(targetId, 5);
        const dataEntries = (request.operation === DhtRpc_1.RecursiveOperation.FETCH_DATA)
            ? Array.from(this.options.localDataStore.values(targetId))
            : [];
        if (request.operation === DhtRpc_1.RecursiveOperation.DELETE_DATA) {
            this.options.localDataStore.markAsDeleted(targetId, (0, identifiers_1.toNodeId)(routedMessage.sourcePeer));
        }
        if ((0, utils_1.areEqualBinaries)(this.options.localPeerDescriptor.nodeId, routedMessage.target)) {
            // TODO this is also very similar case to what we do at line 255, could simplify the code paths?
            this.sendResponse(routedMessage.routingPath, routedMessage.sourcePeer, request.sessionId, closestConnectedNodes, dataEntries, true);
            return (0, RouterRpcLocal_1.createRouteMessageAck)(routedMessage);
        }
        else {
            const ack = this.options.router.doRouteMessage(routedMessage, RoutingSession_1.RoutingMode.RECURSIVE, excludedPeer);
            if ((ack.error === undefined) || (ack.error === DhtRpc_1.RouteMessageError.NO_TARGETS)) {
                const noCloserContactsFound = (ack.error === DhtRpc_1.RouteMessageError.NO_TARGETS) ||
                    (closestConnectedNodes.length > 0
                        && (0, getPreviousPeer_1.getPreviousPeer)(routedMessage)
                        && !this.isPeerCloserToIdThanSelf(closestConnectedNodes[0], targetId));
                this.sendResponse(routedMessage.routingPath, routedMessage.sourcePeer, request.sessionId, closestConnectedNodes, dataEntries, noCloserContactsFound);
            }
            return ack;
        }
    }
    getClosestConnectedNodes(referenceId, limit) {
        const connectedNodes = this.options.connectionsView.getConnections().map((c) => this.options.createDhtNodeRpcRemote(c));
        const sorted = new SortedContactList_1.SortedContactList({
            referenceId,
            maxSize: limit,
            allowToContainReferenceId: true
        });
        sorted.addContacts(connectedNodes);
        return sorted.getClosestContacts(limit).map((peer) => peer.getPeerDescriptor());
    }
    isPeerCloserToIdThanSelf(peer, nodeIdOrDataKey) {
        const nodeIdOrDataKeyRaw = (0, identifiers_1.toDhtAddressRaw)(nodeIdOrDataKey);
        const distance1 = (0, PeerManager_1.getDistance)(peer.nodeId, nodeIdOrDataKeyRaw);
        const distance2 = (0, PeerManager_1.getDistance)(this.options.localPeerDescriptor.nodeId, nodeIdOrDataKeyRaw);
        return distance1 < distance2;
    }
    stop() {
        this.stopped = true;
        this.ongoingSessions.forEach((session, _id) => {
            session.stop();
        });
        this.ongoingSessions.clear();
    }
}
exports.RecursiveOperationManager = RecursiveOperationManager;
//# sourceMappingURL=RecursiveOperationManager.js.map